import { useRef, useState } from "react";

import { CustomLabel, Switch } from "@/components/atoms";
import { Dialog } from "@/components/ui/dialog";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { wordToUpper } from "@/utils/wordToUpper";

import DeactivateConfirm from "./ActionConfirm";
import DeactivateSuccessfully from "./ActionSuccessfully";
import type { ActionStateType, ConfirmType } from "./type";

interface DeactivateSwitchProps {
  state: ActionStateType;
  name?: string;
  onConfirm: ConfirmType;
  active?: boolean;
  disabled?: boolean;
}

export const DeactivateSwitch = ({
  state,
  name = DEFAULT_NAME,
  onConfirm,
  active = true,
  disabled = false,
}: DeactivateSwitchProps) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [checked, setChecked] = useState(active);
  const [isSend, setSendStatus] = useState(false);
  const [isConfirmPending, setIsConfirmPending] = useState(false);
  const confirmLockRef = useRef(false);

  const stateToUpper = wordToUpper(state);

  const resetConfirmState = () => {
    confirmLockRef.current = false;
    setIsConfirmPending(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetConfirmState();
    }
  };

  const handleSwitchClick = () => {
    resetConfirmState();
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (confirmLockRef.current) {
      return;
    }

    confirmLockRef.current = true;
    setIsConfirmPending(true);

    try {
      const { isSuccess, isClose } = await onConfirm();

      if (isSuccess) {
        setChecked(prev => !prev);
        setDialogOpen(false);
        setSendStatus(true);
        return;
      }

      if (isClose) {
        setDialogOpen(false);
        return;
      }
    } catch {
      // Allow retry when the dialog stays open.
    }

    resetConfirmState();
  };

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <div className="flex items-center gap-2 mr-[6px]">
          <Switch
            id="active"
            checked={checked}
            data-testid="switch"
            onClick={handleSwitchClick}
            disabled={disabled}
          />
          <CustomLabel htmlFor="active" label={`Active ${state}`} />
        </div>

        <DeactivateConfirm
          state={state}
          name={name}
          actionName={checked ? "deactivate" : "activate"}
          btnConfirmTitle={`${checked ? "Deactivate" : "Activate"} ${state}`}
          callback={handleConfirm}
          isPending={isConfirmPending}
        />
      </Dialog>

      {isSend && (
        <DeactivateSuccessfully
          userName={name}
          state={stateToUpper}
          callback={isOpen => !isOpen && setSendStatus(false)}
          successAction={checked ? "activated" : "deactivated"}
        />
      )}
    </>
  );
};
